import React, { useRef, useEffect } from 'react';
import { EmptyState } from '../ui';
import { InventoryItem, StaffMember } from '../../lib/types';
import { formatDate } from '../../lib/utils';
import { actionLabel, qtyDisplay, resolveStaff, getItemStats } from './helpers';

interface Props {
  filteredInventory: InventoryItem[];
  lowThreshold: number;
  locale: string;
  search: string;
  deptMap: Record<string, string>;
  staffMap: Record<string, string>;
  staffDirectory: StaffMember[];
  log: any[];
  expandedItemId: string | null;
  onToggleHistory: (id: string) => void;
  renameItemId: string | null;
  renameValue: string; setRenameValue: (s: string) => void;
  renameBusy: boolean;
  onStartRename: (item: InventoryItem) => void;
  onCancelRename: () => void;
  onSubmitRename: (item: InventoryItem) => void;
  onStartMerge: (id: string) => void;
  onCloseExpansion: () => void;
  rowMenuId: string | null;
  setRowMenuId: (id: string | null) => void;
  rowMenuPos: { top: number; right: number };
  setRowMenuPos: (p: { top: number; right: number }) => void;
  onOpenIssue: (id: string) => void;
  onOpenReturn: (id: string) => void;
  onDeleteItem: (id: string, name: string) => void;
  actionId: string | null;
  actionType: 'issue' | 'return' | null;
  actionQty: number; setActionQty: (n: number) => void;
  actionStaff: string; setActionStaff: (s: string) => void;
  actionNotes: string; setActionNotes: (s: string) => void;
  actionBusy: boolean;
  onActionConfirm: () => void;
  onActionCancel: () => void;
  onAddClick: () => void;
}

export function InventoryItemsTable(p: Props) {
  const rowMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!p.rowMenuId) return;
    const handler = (e: MouseEvent) => { if (rowMenuRef.current && !rowMenuRef.current.contains(e.target as Node)) p.setRowMenuId(null); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [p.rowMenuId]);

  const cardStyle: React.CSSProperties = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg, 16px)', padding: 16, boxShadow: 'var(--shadow-sm)' };
  const textBtnStyle: React.CSSProperties = { background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 500, color: 'var(--accent)', padding: '4px 8px', fontFamily: 'var(--font, inherit)' };
  const thStyle: React.CSSProperties = { textAlign: 'left', padding: '6px 8px', fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em' };

  return (
    <>
      {/* Row actions menu (fixed overlay) */}
      {p.rowMenuId && (() => {
        const menuItem = p.filteredInventory.find(i => i.id === p.rowMenuId);
        if (!menuItem) return null;
        return (
          <div ref={rowMenuRef} className="menu" style={{ position: 'fixed', top: p.rowMenuPos.top, right: p.rowMenuPos.right, zIndex: 99999, minWidth: 180 }}>
            <button className="menu-item" style={{ color: 'var(--blue)' }} onClick={() => { p.setRowMenuId(null); p.onOpenIssue(menuItem.id); }}>
              <svg viewBox="0 0 16 16" style={{ width: 14, height: 14, fill: 'none', stroke: 'var(--blue)', strokeWidth: 1.3 }}><path d="M4 8h8M9 5l3 3-3 3"/></svg>
              <span className="flex-1">Issue stock</span>
            </button>
            <button className="menu-item" style={{ color: 'var(--purple)' }} onClick={() => { p.setRowMenuId(null); p.onOpenReturn(menuItem.id); }}>
              <svg viewBox="0 0 16 16" style={{ width: 14, height: 14, fill: 'none', stroke: 'var(--purple)', strokeWidth: 1.3 }}><path d="M12 8H4M7 11l-3-3 3-3"/></svg>
              <span className="flex-1">Return stock</span>
            </button>
            <div className="menu-sep" />
            <button className="menu-item is-danger" onClick={() => { p.setRowMenuId(null); p.onDeleteItem(menuItem.id, menuItem.name); }}>
              <svg viewBox="0 0 16 16" style={{ width: 14, height: 14, fill: 'none', stroke: 'var(--red)', strokeWidth: 1.3 }}><path d="M3 4h10M6 4V3h4v1M5 4v8.5c0 .5.5 1 1 1h4c.5 0 1-.5 1-1V4"/></svg>
              <span className="flex-1">Delete item</span>
            </button>
          </div>
        );
      })()}

      <div key="items" style={{ animation: 'fadeSlideIn .3s ease-out' }}>
        <div className="tw" style={{ marginBottom: 16 }}>
          <table>
            <thead><tr>
              <th style={{ width: 36 }}>#</th>
              <th style={{ minWidth: 160 }}>Item</th>
              <th>Size</th>
              <th>Code</th>
              <th>Dept</th>
              <th style={{ textAlign: 'center' }}>Available</th>
              <th style={{ textAlign: 'center' }}>Original</th>
              <th style={{ textAlign: 'center' }}>Issued</th>
              <th>Status</th>
              <th style={{ width: 36 }}></th>
            </tr></thead>
            <tbody>
              {p.filteredInventory.length === 0 ? (
                <tr><td colSpan={10} style={{ padding: 0 }}>
                  <EmptyState
                    icon={
                      <svg viewBox="0 0 32 32" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="6" y="8" width="20" height="18" rx="3" />
                        <path d="M11 8V6a3 3 0 013-3h4a3 3 0 013 3v2" />
                        <path d="M16 14v6M13 17h6" />
                      </svg>
                    }
                    title={p.search ? 'No items match your search' : 'Nothing in stock yet'}
                    description={p.search ? 'Try a different search term, or clear the search.' : 'Items added from delivered orders will appear here. You can also add items manually.'}
                    action={p.search ? undefined : { label: '+ Add item', onClick: p.onAddClick }}
                  />
                </td></tr>
              ) : p.filteredInventory.map((item, idx) => {
                const isLow = item.available <= p.lowThreshold;
                const isOut = item.available === 0;
                const isActionRow = p.actionId === item.id;
                const isExpanded = p.expandedItemId === item.id;
                const itemLog = isExpanded ? p.log.filter(e => e.item_id === item.id).slice(0, 20) : [];
                const itemStats = isExpanded ? getItemStats(p.log, item.id) : null;
                const deptName = p.deptMap[item.department_id] || '—';
                return (
                  <React.Fragment key={item.id}>
                    <tr
                      onClick={() => p.onToggleHistory(item.id)}
                      style={{ cursor: 'pointer', transition: 'background 0.15s ease' }}
                      onMouseEnter={e => (e.currentTarget as HTMLTableRowElement).style.background = 'var(--surface2)'}
                      onMouseLeave={e => (e.currentTarget as HTMLTableRowElement).style.background = ''}
                    >
                      <td onClick={e => e.stopPropagation()}><span className="mono">{idx + 1}</span></td>
                      <td><strong>{item.name}</strong></td>
                      <td className="mono">{item.size || '—'}</td>
                      <td className="mono" style={{ fontSize: 9, color: 'var(--text3)' }}>{item.code || '—'}</td>
                      <td style={{ fontSize: 12, color: 'var(--text2)' }}>{deptName}</td>
                      <td className="mono" style={{ fontWeight: 700, color: isOut ? 'var(--red)' : isLow ? 'var(--amber)' : 'var(--teal)', textAlign: 'center' }}>{item.available}</td>
                      <td className="mono" style={{ textAlign: 'center' }}>{item.original}</td>
                      <td className="mono" style={{ textAlign: 'center' }}>{item.issued > 0 ? item.issued : '—'}</td>
                      <td>{isOut ? <span className="pill red">Out of stock</span> : isLow ? <span className="pill amber">Low stock</span> : <span className="pill green">In stock</span>}</td>
                      <td style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
                        <button className="row-menu-btn" onClick={(e) => {
                          if (p.rowMenuId === item.id) { p.setRowMenuId(null); }
                          else {
                            const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                            p.setRowMenuPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
                            p.setRowMenuId(item.id);
                          }
                        }} title="Actions">
                          <svg viewBox="0 0 16 16"><circle cx="8" cy="3.5" r="1.1"/><circle cx="8" cy="8" r="1.1"/><circle cx="8" cy="12.5" r="1.1"/></svg>
                        </button>
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr><td colSpan={10} style={{ padding: 0, border: 'none' }}>
                        <div style={{ ...cardStyle, margin: '4px 12px 8px', animation: 'fadeSlideIn .25s ease-out' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                            <div>
                              {p.renameItemId === item.id ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <input
                                    value={p.renameValue}
                                    onChange={e => p.setRenameValue(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') p.onSubmitRename(item); if (e.key === 'Escape') p.onCancelRename(); }}
                                    autoFocus
                                    style={{ fontSize: 13, fontWeight: 600, padding: '5px 8px', borderRadius: 6, border: '1.5px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', fontFamily: 'var(--font, inherit)' }}
                                  />
                                  <button onClick={() => p.onSubmitRename(item)} disabled={p.renameBusy} className="hbtn acc" style={{ borderRadius: 6, padding: '4px 12px', fontSize: 11, fontWeight: 600 }}>{p.renameBusy ? 'Saving…' : 'Save'}</button>
                                  <button onClick={p.onCancelRename} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--text3)', padding: '4px 6px', fontFamily: 'var(--font, inherit)' }}>Cancel</button>
                                </div>
                              ) : (
                                <>
                                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{item.name}</span>
                                  <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 8 }}>Transaction history</span>
                                  <div style={{ display: 'flex', gap: 12, marginTop: 3 }}>
                                    <span style={{ fontSize: 12, color: 'var(--text2)' }}>Department: {deptName}</span>
                                    {item.location && (<span style={{ fontSize: 12, color: 'var(--text2)' }}>Location: {item.location}</span>)}
                                  </div>
                                </>
                              )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0, marginLeft: 12 }}>
                              {p.renameItemId !== item.id && (<>
                                <button style={textBtnStyle} onClick={() => p.onStartRename(item)}
                                  onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                                  onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>Rename</button>
                                <button style={textBtnStyle} onClick={() => p.onStartMerge(item.id)}
                                  onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                                  onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>Merge</button>
                              </>)}
                              <button onClick={p.onCloseExpansion} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--text3)', padding: '2px 6px' }}>✕</button>
                            </div>
                          </div>

                          {itemStats && (
                            <div style={{ display: 'flex', gap: 16, marginBottom: 12, fontSize: 11, color: 'var(--text2)' }}>
                              <span>Received: <strong style={{ color: 'var(--teal)' }}>{itemStats.totalReceived}</strong></span>
                              <span>Issued: <strong style={{ color: 'var(--blue)' }}>{itemStats.totalIssued}</strong></span>
                              <span>Returned: <strong style={{ color: 'var(--purple)' }}>{itemStats.totalReturned}</strong></span>
                              <span>Current stock: <strong style={{ color: 'var(--text)' }}>{item.available}</strong></span>
                            </div>
                          )}

                          {itemLog.length === 0 ? (
                            <div style={{ color: 'var(--text3)', fontSize: 12, textAlign: 'center', padding: '16px 0' }}>No transactions yet</div>
                          ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                              <thead>
                                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                                  <th style={thStyle}>Date</th>
                                  <th style={thStyle}>Action</th>
                                  <th style={{ ...thStyle, textAlign: 'right' }}>Qty</th>
                                  <th style={thStyle}>Staff / Notes</th>
                                </tr>
                              </thead>
                              <tbody>
                                {itemLog.map(entry => {
                                  const label = actionLabel(entry.action);
                                  const qty = qtyDisplay(entry.action, entry.quantity);
                                  return (
                                    <tr key={entry.id} style={{ borderBottom: '1px solid var(--border)' }}>
                                      <td style={{ padding: '6px 8px', fontFamily: 'var(--font-mono)', color: 'var(--text2)' }}>{formatDate(entry.timestamp, p.locale)}</td>
                                      <td style={{ padding: '6px 8px' }}>
                                        <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, color: label.color, background: label.bg }}>{label.text}</span>
                                      </td>
                                      <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 600, color: qty.color }}>{qty.symbol}</td>
                                      <td style={{ padding: '6px 8px', color: 'var(--text2)', fontSize: 11 }}>{resolveStaff(entry, p.staffMap)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </td></tr>
                    )}

                    {isActionRow && (
                      <tr><td colSpan={10} style={{ padding: '8px 12px', background: 'var(--bg)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>
                            {p.actionType === 'issue' ? '📤 Issue' : '📥 Return'} — {item.name}
                          </span>
                          <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                            {p.actionType === 'issue' ? `(${item.available} available)` : `(${item.issued} issued)`}
                          </span>
                          <input type="number" value={p.actionQty} min={1}
                            max={p.actionType === 'issue' ? item.available : item.issued}
                            onChange={e => p.setActionQty(parseInt(e.target.value) || 1)}
                            style={{ width: 70, padding: '5px 8px', fontSize: 12, borderRadius: 6, border: '1px solid var(--border)', textAlign: 'center', fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)' }} />
                          <input list="action-staff-list" value={p.actionStaff} onChange={e => p.setActionStaff(e.target.value)}
                            placeholder={p.actionType === 'issue' ? 'Issued to…' : 'Returned by…'}
                            style={{ width: 150, padding: '5px 8px', fontSize: 12, borderRadius: 6, border: '1px solid var(--border)', fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)' }} />
                          <datalist id="action-staff-list">
                            {p.staffDirectory.filter(s => s.active !== false).map(s => <option key={s.id} value={s.name} />)}
                          </datalist>
                          <input value={p.actionNotes} onChange={e => p.setActionNotes(e.target.value)}
                            placeholder="Notes (e.g. location, reason…)"
                            style={{ width: 200, padding: '5px 8px', fontSize: 12, borderRadius: 6, border: '1px solid var(--border)', fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)' }} />
                          <button onClick={p.onActionConfirm} disabled={p.actionBusy} className="hbtn acc" style={{ borderRadius: 6, padding: '5px 14px', fontSize: 11, fontWeight: 600 }}>{p.actionBusy ? 'Saving…' : 'Confirm'}</button>
                          <button onClick={p.onActionCancel} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '5px 10px', fontSize: 11, color: 'var(--text2)', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
                        </div>
                      </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
